import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import {
  Database,
  Download,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Loader2,
  FolderOpen,
  Upload,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const BackupManagementPage = () => {
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [status, setStatus] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState('live');
  const [triggeringBackup, setTriggeringBackup] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [restoreBackup, setRestoreBackup] = useState(null);
  const [backupToDelete, setBackupToDelete] = useState(null);
  const [selectedBackupIds, setSelectedBackupIds] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [restoring, setRestoring] = useState(false);
  const [restorePrecheckLoading, setRestorePrecheckLoading] = useState(false);
  const [restorePrecheck, setRestorePrecheck] = useState(null);
  const [restorePrecheckError, setRestorePrecheckError] = useState('');
  const [restorePrecheckCollectionsSignature, setRestorePrecheckCollectionsSignature] = useState('');
  const [restoreProgressDialogOpen, setRestoreProgressDialogOpen] = useState(false);
  const [restoreSession, setRestoreSession] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const hasInitializedFolderRef = useRef(false);

  const folders = {
    live: { id: '1H15fc_R8Znwj-YuaaVoybsuEp5QOKW2R', name: 'Live', color: 'bg-green-100 text-green-800' },
    dev: { id: '1fDLRRSoM-DANSecj09iYeSePd65Maw8V', name: 'Dev', color: 'bg-blue-100 text-blue-800' },
  };
  const folderKeys = ['live', 'dev'];

  const getFolderId = (folderKey) => status?.folders?.[folderKey] || folders[folderKey]?.id || '';
  const canDeleteInSelectedFolder = (status?.environment === 'live') || selectedFolder === 'dev';
  const isAllSelectedInCurrentTab = backups.length > 0 && selectedBackupIds.length === backups.length;
  const driveConfigured = status?.drive_configured !== false;
  const driveErrorMessage = status?.drive_error || '';
  const canCreateBackupInCurrentTab = selectedFolder === status?.environment;
  const backupProgressPercent = Math.max(0, Math.min(100, Number(status?.backup_progress_percent || 0)));
  const getCollectionsSignature = (collections = []) => [...collections].map((v) => String(v)).sort().join('|');
  const selectedCollectionsSignature = getCollectionsSignature(selectedCollections);
  const restoreProgressPercent = Math.max(0, Math.min(100, Number(status?.restore_progress_percent || 0)));
  const restoreCollectionsCompleted = Number(status?.restore_collections_completed || 0);
  const restoreCollectionsTotal = Number(status?.restore_total_collections || 0);

  const restorePhase = status?.restore_phase || 'idle';
  const restoreInProgress = status?.restore_in_progress === true;
  const restoreFailed = !restoreInProgress && restorePhase === 'failed';
  const restoreCompleted = !restoreInProgress && restorePhase === 'completed';
  const restoreVerificationMismatchCount = Number(status?.restore_verification_mismatch_count || 0);
  const restoreVerificationMismatchCollections = status?.restore_verification_mismatch_collections || [];
  const restoreVerificationMismatchPreview = status?.restore_verification_mismatch_preview || [];
  const restoreVerificationPassed = status?.restore_verification_passed;
  const restoreVerificationSkippedCount = Number(status?.restore_verification_skipped_count || 0);
  const restoreVerificationSkippedCollections = status?.restore_verification_skipped_collections || [];
  const restoreCollectionCountDetails = status?.restore_collection_count_details || [];
  const restorePrecheckRows = restorePrecheck?.rows || [];
  const restorePrecheckSummary = restorePrecheck?.summary || {};
  const restorePrecheckReady = Boolean(restorePrecheck)
    && restorePrecheckCollectionsSignature === selectedCollectionsSignature
    && !restorePrecheckLoading
    && !restorePrecheckError;

  const getRestoreStepStatus = (stepKey) => {
    const preBackupSkipped = status?.restore_pre_backup_skipped === true;
    const preBackupPhases = ['queued', 'creating_pre_backup'];
    const preparePhases = ['pre_backup_completed', 'preparing_restore', 'downloading_backup'];
    const restorePhases = ['restoring_via_utility', 'restoring_collections', 'finalizing_restore', 'verifying_restore'];

    if (stepKey === 'pre_backup') {
      if (preBackupSkipped) return 'skipped';
      if (preBackupPhases.includes(restorePhase)) return 'active';
      if ([...preparePhases, ...restorePhases, 'completed'].includes(restorePhase)) return 'completed';
      if (restorePhase === 'failed') return 'failed';
      return 'pending';
    }

    if (stepKey === 'prepare_restore') {
      if (preparePhases.includes(restorePhase)) return 'active';
      if ([...restorePhases, 'completed'].includes(restorePhase)) return 'completed';
      if (restorePhase === 'failed') return 'failed';
      return 'pending';
    }

    if (stepKey === 'restore_progress') {
      if (restorePhases.includes(restorePhase)) return 'active';
      if (restorePhase === 'completed') return 'completed';
      if (restorePhase === 'failed') return 'failed';
      return 'pending';
    }

    if (stepKey === 'result') {
      if (restorePhase === 'completed') return 'completed';
      if (restorePhase === 'failed') return 'failed';
      if (restoreInProgress) return 'active';
      return 'pending';
    }

    return 'pending';
  };

  const getStepDotClass = (statusValue) => {
    if (statusValue === 'completed') return 'bg-emerald-600 text-white';
    if (statusValue === 'skipped') return 'bg-slate-400 text-white';
    if (statusValue === 'active') return 'bg-blue-600 text-white animate-pulse';
    if (statusValue === 'failed') return 'bg-red-600 text-white';
    return 'bg-slate-200 text-slate-500';
  };

  useEffect(() => {
    const load = async () => {
      const statusData = await fetchStatus();
      if (statusData?.environment && !hasInitializedFolderRef.current) {
        hasInitializedFolderRef.current = true;
        if (statusData.environment !== selectedFolder) {
          setSelectedFolder(statusData.environment);
          return;
        }
      }
      if (statusData?.drive_configured === false) {
        setBackups([]);
        setLoading(false);
        return;
      }
      fetchBackups(selectedFolder, statusData);
    };

    load();
  }, [selectedFolder]);

  useEffect(() => {
    setSelectedBackupIds([]);
  }, [selectedFolder, backups.length]);

  useEffect(() => {
    if (!status?.backup_in_progress) return undefined;

    const interval = setInterval(async () => {
      const latestStatus = await fetchStatus();
      if (latestStatus?.drive_configured !== false) {
        fetchBackups(selectedFolder, latestStatus);
      }
    }, 8000);

    return () => clearInterval(interval);
  }, [status?.backup_in_progress, selectedFolder]);

  useEffect(() => {
    if (status?.restore_in_progress) {
      setRestoreProgressDialogOpen(true);
    }
  }, [status?.restore_in_progress]);

  useEffect(() => {
    if (!restoreDialogOpen || !restoreBackup?.id) return undefined;
    if (selectedCollections.length === 0) return undefined;

    const latestSignature = getCollectionsSignature(selectedCollections);
    if (latestSignature === restorePrecheckCollectionsSignature) return undefined;

    const timer = setTimeout(() => {
      runRestorePrecheck(restoreBackup, selectedCollections);
    }, 450);

    return () => clearTimeout(timer);
  }, [restoreDialogOpen, restoreBackup?.id, selectedCollections, restorePrecheckCollectionsSignature]);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/api/admin/backup/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch status');
      }

      const data = await response.json();
      setStatus(data);
      return data;
    } catch (error) {
      console.error('Failed to fetch backup status:', error);
      toast.error('Failed to fetch backup status');
      return null;
    }
  };

  const fetchBackups = async (folderKey = selectedFolder, statusData = status) => {
    setLoading(true);
    try {
      if (statusData?.drive_configured === false) {
        setBackups([]);
        return;
      }

      const token = localStorage.getItem('token');
      const folderId = getFolderId(folderKey);
      if (!folderId) {
        setBackups([]);
        return;
      }

      const response = await fetch(`${getApiBaseUrl()}/api/admin/backup/list?folder_id=${folderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch backups');
      }

      const data = await response.json();
      setBackups(data.backups || []);
    } catch (error) {
      console.error('Failed to fetch backups:', error);
      toast.error('Failed to fetch backups');
    } finally {
      setLoading(false);
    }
  };

  const runRestorePrecheck = async (backup = restoreBackup, collections = selectedCollections) => {
    if (!backup?.id) return null;

    setRestorePrecheckLoading(true);
    setRestorePrecheckError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/api/admin/backup/restore-precheck`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          backup_file_id: backup.id,
          collections,
          folder_id: getFolderId(selectedFolder),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to run restore pre-check');
      }

      const data = await response.json();
      setRestorePrecheck(data);
      setRestorePrecheckCollectionsSignature(getCollectionsSignature(collections));
      return data;
    } catch (error) {
      console.error('Restore pre-check failed:', error);
      setRestorePrecheckError(error.message || 'Failed to run restore pre-check');
      return null;
    } finally {
      setRestorePrecheckLoading(false);
    }
  };

  const triggerBackup = async () => {
    setTriggeringBackup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/api/admin/backup/trigger`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to trigger backup');
      }

      const data = await response.json();
      if (data.queued) {
        toast.success(data.message || 'Backup queued successfully. Refresh in a minute to see it in the list.');
      } else {
        toast.success(
          <div>
            <p className="font-medium">Backup completed successfully!</p>
            <p className="text-sm text-gray-600">{data.file_name}</p>
            <p className="text-xs text-gray-500">Size: {((Number(data.file_size || 0)) / 1024 / 1024).toFixed(2)} MB</p>
          </div>
        );
      }

      fetchStatus();
      setTimeout(() => {
        fetchBackups();
        fetchStatus();
      }, 3000);
    } catch (error) {
      console.error('Backup failed:', error);
      toast.error(error.message || 'Failed to create backup');
    } finally {
      setTriggeringBackup(false);
    }
  };

  const handleOpenRestoreDialog = (backup) => {
    const initialCollections = status?.collections || [];
    setRestoreBackup(backup);
    setSelectedCollections(initialCollections);
    setRestorePrecheck(null);
    setRestorePrecheckError('');
    setRestorePrecheckCollectionsSignature('');
    setRestoreDialogOpen(true);
    if (initialCollections.length > 0) {
      runRestorePrecheck(backup, initialCollections);
    }
  };

  const handleRestore = async () => {
    if (!restoreBackup || selectedCollections.length === 0) {
      toast.error('Please select at least one collection to restore');
      return;
    }

    if (restorePrecheckLoading) {
      toast.error('Pre-check is still running. Please wait.');
      return;
    }

    if (!restorePrecheck || restorePrecheckCollectionsSignature !== selectedCollectionsSignature) {
      const latestPrecheck = await runRestorePrecheck(restoreBackup, selectedCollections);
      if (!latestPrecheck) {
        toast.error('Pre-check failed. Please retry before restore.');
        return;
      }
    }

    if (restorePrecheckError) {
      toast.error('Fix pre-check errors before confirming restore.');
      return;
    }

    setRestoring(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/api/admin/backup/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          backup_file_id: restoreBackup.id,
          collections: selectedCollections,
          folder_id: getFolderId(selectedFolder),
          create_pre_backup: selectedFolder !== 'live' || status?.environment === 'live'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Restore failed');
      }

      const data = await response.json();

      if (data.queued) {
        toast.success(data.message || 'Restore queued successfully.');
        setRestoreSession({
          backupName: restoreBackup?.name || 'selected-backup.zip',
          queuedAt: new Date().toISOString(),
        });
        setRestoreProgressDialogOpen(true);
      } else {
        toast.success(
          <div>
            <p className="font-medium">Restore completed!</p>
            {data.pre_backup_file && (
              <p className="text-xs text-gray-500">Safety backup: {data.pre_backup_file}</p>
            )}
          </div>
        );
      }

      setRestoreDialogOpen(false);
      setTimeout(() => {
        fetchBackups();
        fetchStatus();
      }, 3000);
    } catch (error) {
      console.error('Restore failed:', error);
      toast.error(error.message || 'Failed to restore backup');
    } finally {
      setRestoring(false);
    }
  };

  const handleDelete = async () => {
    if (!backupToDelete) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/api/admin/backup/delete/${backupToDelete.id}?folder_key=${selectedFolder}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete backup');
      }

      toast.success('Backup deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedBackupIds((prev) => prev.filter((id) => id !== backupToDelete.id));
      fetchBackups();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete backup');
    } finally {
      setDeleting(false);
    }
  };

  const toggleBackupSelection = (backupId) => {
    setSelectedBackupIds((prev) => (
      prev.includes(backupId)
        ? prev.filter((id) => id !== backupId)
        : [...prev, backupId]
    ));
  };

  const toggleSelectAllBackups = () => {
    if (isAllSelectedInCurrentTab) {
      setSelectedBackupIds([]);
      return;
    }
    setSelectedBackupIds(backups.map((backup) => backup.id));
  };

  const handleBulkDelete = async () => {
    if (selectedBackupIds.length === 0) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/api/admin/backup/delete-bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          folder_key: selectedFolder,
          file_ids: selectedBackupIds
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to bulk delete backups');
      }

      const result = await response.json();
      if (result.failed_count > 0) {
        toast.error(`Deleted ${result.deleted_count}, failed ${result.failed_count}.`);
      } else {
        toast.success(`Deleted ${result.deleted_count} backup(s).`);
      }

      setBulkDeleteDialogOpen(false);
      setSelectedBackupIds([]);
      fetchBackups();
      fetchStatus();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error(error.message || 'Failed to bulk delete backups');
    } finally {
      setDeleting(false);
    }
  };

  const toggleCollection = (collection) => {
    setSelectedCollections(prev =>
      prev.includes(collection)
        ? prev.filter(c => c !== collection)
        : [...prev, collection]
    );
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getBackupType = (filename) => {
    if (filename.includes('manual')) return { label: 'Manual', color: 'bg-blue-100 text-blue-800' };
    if (filename.includes('auto')) return { label: 'Auto', color: 'bg-green-100 text-green-800' };
    return { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };
  };

  const mongoSourceLabel = (status?.mongo_source || 'unknown').toUpperCase();
  const mongoHostLabel = status?.mongo_host || 'local';

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Database className="w-8 h-8 text-blue-600" />
              Backup Management
            </h1>
            <p className="text-gray-500">Restore Google Drive backups into this database, or save a Dev backup.</p>
            {status?.db_name && (
              <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="backup-management-db-context">
                <span
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${
                    status.environment === 'live'
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                      : 'bg-red-50 text-red-800 border-red-300'
                  }`}
                  data-testid="backup-management-environment-badge"
                >
                  <span className={`inline-block w-2 h-2 rounded-full ${status.environment === 'live' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  {status.environment === 'live' ? 'LIVE' : 'DEV'}
                </span>
                <span className="font-mono text-sm text-gray-700 bg-gray-100 border border-gray-200 px-2.5 py-0.5 rounded" data-testid="backup-management-db-name-badge">
                  {status.db_name}
                </span>
                <span className="text-xs text-gray-500" data-testid="backup-management-mongo-source-value">
                  {mongoSourceLabel} Mongo · {mongoHostLabel}
                </span>
                <span
                  className={`inline-flex items-center gap-1 text-xs ${driveConfigured ? 'text-emerald-700' : 'text-red-700'}`}
                  data-testid="backup-management-drive-status"
                >
                  {driveConfigured ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                  Drive {driveConfigured ? 'connected' : 'not connected'}
                </span>
              </div>
            )}
          </div>

          <Button
            onClick={triggerBackup}
            disabled={triggeringBackup || status?.backup_in_progress || !canCreateBackupInCurrentTab}
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 shrink-0"
            data-testid="backup-create-manual-backup-button"
          >
            {triggeringBackup ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating Backup...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Create Manual Backup
              </>
            )}
          </Button>
        </div>

        {!canCreateBackupInCurrentTab && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700" data-testid="backup-create-scope-info">
            <p className="text-xs">
              Manual backup is saved to the {status?.environment === 'live' ? 'Live' : 'Dev'} Drive folder. Switch to that tab to create one.
            </p>
          </div>
        )}

        {status?.backup_in_progress && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-blue-800" data-testid="backup-operation-in-progress-alert">
            <p className="text-sm font-semibold">Backup operation in progress</p>
            <p className="text-xs mt-1">
              {status?.backup_phase ? `Phase: ${status.backup_phase}` : 'Working...'}
              {status?.backup_current_collection ? ` · ${status.backup_current_collection}` : ''}
            </p>
            <div className="mt-2" data-testid="backup-progress-bar-container">
              <div className="h-2.5 w-full rounded-full bg-blue-100 overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{ width: `${backupProgressPercent}%` }}
                  data-testid="backup-progress-bar-fill"
                />
              </div>
            </div>
          </div>
        )}

        {!status?.backup_in_progress && status?.backup_last_error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800" data-testid="backup-last-error-alert">
            <p className="text-sm font-semibold">Last backup error</p>
            <p className="text-xs mt-1">{status.backup_last_error}</p>
          </div>
        )}

        {!driveConfigured && (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800"
            data-testid="backup-drive-not-configured-alert"
          >
            <p className="text-sm font-semibold">Google Drive is not connected</p>
            <p className="text-xs mt-1">
              {driveErrorMessage || 'Add the Google service account so this page can list and restore backups.'}
            </p>
          </div>
        )}

        {/* Backups Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Backup Files</CardTitle>
                <CardDescription>Live is production Drive. Dev is this app’s Drive folder. Restore always writes to {status?.db_name || 'the active database'}.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteDialogOpen(true)}
                  disabled={!driveConfigured || selectedBackupIds.length === 0 || deleting || !canDeleteInSelectedFolder}
                  data-testid="backup-bulk-delete-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected ({selectedBackupIds.length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchBackups}
                  disabled={!driveConfigured || loading}
                  data-testid="backup-refresh-button"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={selectedFolder} onValueChange={setSelectedFolder}>
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="live" className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  Live
                </TabsTrigger>
                <TabsTrigger value="dev" className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  Dev
                </TabsTrigger>
              </TabsList>

              {/* Cross-environment restore hint — shown on Dev when viewing Live tab */}
              {status?.environment === 'dev' && selectedFolder === 'live' && (
                <div className="flex items-start gap-3 mb-4 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
                  <span className="text-lg leading-none">💡</span>
                  <div>
                    <p className="font-semibold">Restoring from Live → Dev</p>
                    <p className="text-blue-700 mt-0.5">
                      You are on the <strong>Dev</strong> environment viewing Live backups.
                      Clicking <strong>Restore</strong> will overwrite your local dev database (<code className="bg-blue-100 px-1 rounded text-xs">{status?.db_name || 'test_PerfectAttire'}</code>) with data from the selected live backup — great for syncing dev with the latest production data.
                      A safety backup of your current dev data will be created first.
                    </p>
                  </div>
                </div>
              )}

              {!canDeleteInSelectedFolder && (
                <div
                  className="flex items-start gap-3 mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800"
                  data-testid="backup-live-delete-restriction-alert"
                >
                  <span className="text-lg leading-none">🛑</span>
                  <div>
                    <p className="font-semibold">Cannot delete Live backups from DEV</p>
                    <p className="text-red-700 mt-0.5">Switch to a Live environment to delete production Drive files.</p>
                  </div>
                </div>
              )}

              {folderKeys.map((folder) => (
                <TabsContent key={folder} value={folder}>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                  ) : backups.length === 0 ? (
                    <div className="text-center py-12">
                      <Database className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-500">No backups found in {folders[folder].name} folder</p>
                    </div>
                  ) : (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <Checkbox
                                checked={isAllSelectedInCurrentTab}
                                onCheckedChange={toggleSelectAllBackups}
                                disabled={!canDeleteInSelectedFolder || backups.length === 0}
                                data-testid={`backup-select-all-${folder}-checkbox`}
                              />
                            </TableHead>
                            <TableHead>File Name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Size</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {backups.map((backup) => {
                            const backupType = getBackupType(backup.name);
                            return (
                              <TableRow key={backup.id}>
                                <TableCell>
                                  <Checkbox
                                    checked={selectedBackupIds.includes(backup.id)}
                                    onCheckedChange={() => toggleBackupSelection(backup.id)}
                                    disabled={!canDeleteInSelectedFolder}
                                    data-testid={`backup-select-${backup.id}-checkbox`}
                                  />
                                </TableCell>
                                <TableCell className="font-medium font-mono text-sm">
                                  {backup.name}
                                </TableCell>
                                <TableCell>
                                  <Badge className={backupType.color}>
                                    {backupType.label}
                                  </Badge>
                                </TableCell>
                                <TableCell>{formatBytes(backup.size)}</TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span className="text-sm">
                                      {format(new Date(backup.createdTime), 'MMM dd, yyyy HH:mm')}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {formatDistanceToNow(new Date(backup.createdTime), { addSuffix: true })}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleOpenRestoreDialog(backup)}
                                      data-testid={`backup-restore-${backup.id}-button`}
                                    >
                                      <Download className="w-4 h-4 mr-1" />
                                      Restore
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setBackupToDelete(backup);
                                        setDeleteDialogOpen(true);
                                      }}
                                      disabled={!canDeleteInSelectedFolder}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`backup-delete-${backup.id}-button`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Restore Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Restore Backup</DialogTitle>
            <DialogDescription>
              Select collections to restore from backup: <span className="font-mono">{restoreBackup?.name}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="mb-4 rounded-md border bg-slate-50 p-3" data-testid="restore-precheck-db-panel">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold text-slate-700" data-testid="restore-precheck-db-name-label">DB Name being checked</p>
                  <code className="text-sm" data-testid="restore-precheck-db-name-value">
                    {restorePrecheck?.target_db_name || status?.db_name || 'Loading...'}
                  </code>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => runRestorePrecheck(restoreBackup, selectedCollections)}
                  disabled={restorePrecheckLoading || !restoreBackup}
                  data-testid="restore-precheck-rerun-button"
                >
                  {restorePrecheckLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    'Re-run Pre-check'
                  )}
                </Button>
              </div>
              <p className="mt-2 text-xs text-slate-600" data-testid="restore-precheck-summary-line">
                Match: {Number(restorePrecheckSummary?.match_count || 0)} •
                Mismatch: {Number(restorePrecheckSummary?.mismatch_count || 0)} •
                Missing in backup: {Number(restorePrecheckSummary?.missing_in_backup_count || 0)} •
                Unknown expected: {Number(restorePrecheckSummary?.unknown_expected_count || 0)}
              </p>
              {restorePrecheckError && (
                <p className="mt-2 text-xs text-red-700" data-testid="restore-precheck-error-message">
                  {restorePrecheckError}
                </p>
              )}
            </div>

            <details className="mb-4 rounded-md border bg-white" data-testid="restore-precheck-expandable-box">
              <summary className="cursor-pointer list-none px-3 py-2 text-sm font-semibold flex items-center justify-between" data-testid="restore-precheck-expandable-summary">
                <span>Collection Variance Before Restore</span>
                <span className="text-xs text-slate-600">{restorePrecheckRows.length} rows</span>
              </summary>
              <div className="px-3 pb-3" data-testid="restore-precheck-expandable-content">
                {restorePrecheckLoading ? (
                  <div className="py-4 flex items-center justify-center" data-testid="restore-precheck-loading-state">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div className="max-h-56 overflow-auto rounded border" data-testid="restore-precheck-table-scrollbox">
                    <table className="w-full text-xs" data-testid="restore-precheck-table">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-2">Collection</th>
                          <th className="text-right px-2 py-2">Backup Expected</th>
                          <th className="text-right px-2 py-2">Current DB</th>
                          <th className="text-right px-2 py-2">Variance</th>
                          <th className="text-center px-2 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {restorePrecheckRows.map((row) => (
                          <tr key={row.collection} className="border-t" data-testid={`restore-precheck-row-${row.collection}`}>
                            <td className="px-2 py-1.5 font-mono">{row.collection}</td>
                            <td className="px-2 py-1.5 text-right">{row.expected_count ?? '-'}</td>
                            <td className="px-2 py-1.5 text-right">{row.current_db_count ?? '-'}</td>
                            <td className={`px-2 py-1.5 text-right ${Number(row?.variance || 0) === 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                              {row.variance ?? '-'}
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <span
                                className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                  row.status === 'match'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : row.status === 'mismatch'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-amber-100 text-amber-700'
                                }`}
                                data-testid={`restore-precheck-status-${row.collection}`}
                              >
                                {String(row.status || 'unknown').toUpperCase()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </details>

            <Label className="text-sm font-medium mb-3 block">
              Select Collections ({selectedCollections.length} selected)
            </Label>
            <ScrollArea className="h-[300px] rounded-md border p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedCollections(status?.collections || [])}
                    data-testid="restore-dialog-select-all-button"
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedCollections([])}
                    data-testid="restore-dialog-deselect-all-button"
                  >
                    Deselect All
                  </Button>
                </div>

                {status?.collections?.map((collection) => (
                  <div key={collection} className="flex items-center space-x-2">
                    <Checkbox
                      id={collection}
                      checked={selectedCollections.includes(collection)}
                      onCheckedChange={() => toggleCollection(collection)}
                      data-testid={`restore-dialog-collection-${collection}-checkbox`}
                    />
                    <label
                      htmlFor={collection}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {collection}
                    </label>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-900 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                A safety backup will be created before restoration
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreDialogOpen(false)} data-testid="restore-dialog-cancel-button">
              Cancel
            </Button>
            <Button
              onClick={handleRestore}
              disabled={restoring || selectedCollections.length === 0 || !restorePrecheckReady}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="restore-dialog-confirm-button"
            >
              {restoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Confirm Restore
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={restoreProgressDialogOpen}
        onOpenChange={(open) => {
          if (restoreInProgress) return;
          setRestoreProgressDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-2xl" data-testid="restore-progress-dialog">
          <DialogHeader>
            <DialogTitle data-testid="restore-progress-dialog-title">Restore Progress</DialogTitle>
            <DialogDescription data-testid="restore-progress-dialog-description">
              Tracking restore workflow for: <span className="font-mono">{restoreSession?.backupName || restoreBackup?.name || 'selected backup'}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg border bg-slate-50 p-3" data-testid="restore-progress-overall-card">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="font-semibold text-slate-700" data-testid="restore-progress-overall-label">Overall Restore Progress</span>
                <span className="font-mono text-slate-800" data-testid="restore-progress-overall-percent">{restoreProgressPercent.toFixed(1)}%</span>
              </div>
              <p className="mt-1 text-xs text-slate-600" data-testid="restore-progress-target-db-label">
                DB checked: <span className="font-mono" data-testid="restore-progress-target-db-value">{status?.restore_target_db_name || status?.db_name || 'unknown'}</span>
                {status?.restore_source_db_name ? (
                  <>
                    {' '}• Source archive DB: <span className="font-mono" data-testid="restore-progress-source-db-value">{status.restore_source_db_name}</span>
                  </>
                ) : null}
              </p>
              <div className="mt-2 h-2.5 w-full rounded-full bg-slate-200 overflow-hidden" data-testid="restore-progress-overall-bar-container">
                <div
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{ width: `${restoreProgressPercent}%` }}
                  data-testid="restore-progress-overall-bar-fill"
                />
              </div>
            </div>

            <div className="space-y-3" data-testid="restore-progress-steps-container">
              <div className="flex items-start gap-3" data-testid="restore-step-pre-backup-row">
                <div className={`mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${getStepDotClass(getRestoreStepStatus('pre_backup'))}`}>1</div>
                <div>
                  <p className="text-sm font-semibold" data-testid="restore-step-pre-backup-title">
                    {status?.restore_pre_backup_skipped ? 'Safety backup skipped' : 'Creating a backup before restore'}
                  </p>
                  <p className="text-xs text-slate-600" data-testid="restore-step-pre-backup-message">
                    {status?.restore_pre_backup_skipped
                      ? 'Live Drive restore onto Dev does not dump the current Dev database first.'
                      : (status?.restore_pre_backup_file ? `Safety backup: ${status.restore_pre_backup_file}` : 'Preparing safety backup before applying restore.')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="restore-step-prepare-row">
                <div className={`mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${getStepDotClass(getRestoreStepStatus('prepare_restore'))}`}>2</div>
                <div>
                  <p className="text-sm font-semibold" data-testid="restore-step-prepare-title">Backup created. Now preparing to restore</p>
                  <p className="text-xs text-slate-600" data-testid="restore-step-prepare-message">
                    Downloading and validating selected backup archive.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="restore-step-progress-row">
                <div className={`mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${getStepDotClass(getRestoreStepStatus('restore_progress'))}`}>3</div>
                <div className="w-full">
                  <p className="text-sm font-semibold" data-testid="restore-step-progress-title">Restore progress bar</p>
                  <p className="text-xs text-slate-600" data-testid="restore-step-progress-message">
                    Collections: {restoreCollectionsCompleted}/{restoreCollectionsTotal || 0}
                    {status?.restore_current_collection ? ` • Current: ${status.restore_current_collection}` : ''}
                    {restorePhase === 'restoring_via_utility' ? ' • Executing restore on mongodb-tools utility' : ''}
                  </p>
                  <div className="mt-2 h-2 w-full rounded-full bg-slate-200 overflow-hidden" data-testid="restore-step-progress-bar-container">
                    <div
                      className="h-full bg-emerald-600 transition-all duration-300"
                      style={{ width: `${restoreProgressPercent}%` }}
                      data-testid="restore-step-progress-bar-fill"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="restore-step-result-row">
                <div className={`mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${getStepDotClass(getRestoreStepStatus('result'))}`}>4</div>
                <div>
                  <p className="text-sm font-semibold" data-testid="restore-step-result-title">Restore completed or failed</p>
                  <p className="text-xs text-slate-700" data-testid="restore-step-result-message">
                    {restoreInProgress && 'Restore is running...'}
                    {restoreCompleted && 'Restore successfully completed with zero variance across selected collections.'}
                    {restoreFailed && `Restore failed: ${status?.restore_last_error || 'Unknown error'}`}
                    {!restoreInProgress && !restoreCompleted && !restoreFailed && 'Waiting for restore to start...'}
                  </p>
                  {!restoreInProgress && restoreVerificationMismatchCount > 0 && (
                    <p className="text-xs text-red-700 mt-1" data-testid="restore-step-result-mismatch-summary">
                      Verification mismatch: {restoreVerificationMismatchCount} collection(s) failed verification.
                      {restoreVerificationMismatchCollections.length > 0
                        ? ` [${restoreVerificationMismatchCollections.join(', ')}]`
                        : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {restoreFailed && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-800" data-testid="restore-progress-failure-alert">
                <p className="text-sm font-semibold">Restore failed</p>
                <p className="text-xs mt-1">{status?.restore_last_error || 'No backend error message available.'}</p>
                {restoreVerificationMismatchCount > 0 && (
                  <p className="text-xs mt-1" data-testid="restore-progress-failure-variance-details">
                    Count variance is non-zero for {restoreVerificationMismatchCount} collection(s).
                  </p>
                )}
                {restoreVerificationMismatchPreview.length > 0 && (
                  <div className="mt-2 rounded border border-red-200 bg-white p-2" data-testid="restore-progress-failure-mismatch-preview">
                    <p className="text-[11px] font-semibold text-red-700">Mismatch preview:</p>
                    <ul className="mt-1 list-disc pl-4 text-[11px] text-red-700 space-y-0.5">
                      {restoreVerificationMismatchPreview.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {restoreCompleted && restoreVerificationPassed !== false && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-800" data-testid="restore-progress-success-alert">
                <p className="text-sm font-semibold">Restore successfully completed</p>
                <p className="text-xs mt-1">Completed at: {status?.restore_last_completed_at ? new Date(status.restore_last_completed_at).toLocaleString() : 'now'}</p>
                {restoreVerificationSkippedCount > 0 && (
                  <p className="text-xs mt-1 text-amber-700" data-testid="restore-progress-success-skipped-warning">
                    Strict count verification skipped for {restoreVerificationSkippedCount} collection(s)
                    {restoreVerificationSkippedCollections.length > 0
                      ? ` [${restoreVerificationSkippedCollections.slice(0, 10).join(', ')}${restoreVerificationSkippedCollections.length > 10 ? ', ...' : ''}]`
                      : ''}
                    .
                  </p>
                )}
              </div>
            )}

            {restoreCollectionCountDetails.length > 0 && (
              <details
                className="rounded-md border border-slate-200 bg-white"
                data-testid="restore-collection-counts-expandable"
              >
                <summary
                  className="cursor-pointer list-none px-3 py-2 text-sm font-semibold text-slate-800 flex items-center justify-between"
                  data-testid="restore-collection-counts-summary"
                >
                  <span>Collection Counts (Expected vs Actual)</span>
                  <span className="text-xs text-slate-600">{restoreCollectionCountDetails.length} collections</span>
                </summary>
                <div className="px-3 pb-3" data-testid="restore-collection-counts-content">
                  <div className="max-h-64 overflow-auto rounded border" data-testid="restore-collection-counts-scrollbox">
                    <table className="w-full text-xs" data-testid="restore-collection-counts-table">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-2">Collection</th>
                          <th className="text-right px-2 py-2">Expected</th>
                          <th className="text-right px-2 py-2">Pre</th>
                          <th className="text-right px-2 py-2">Post</th>
                          <th className="text-right px-2 py-2">Variance</th>
                          <th className="text-center px-2 py-2">Status</th>
                          <th className="text-left px-2 py-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {restoreCollectionCountDetails.map((row) => {
                          const varianceValue = Number(row?.variance || 0);
                          const isPass = row?.verification_passed === true || row?.verification_skipped === true;
                          return (
                            <tr
                              key={row.collection}
                              className="border-t"
                              data-testid={`restore-collection-count-row-${row.collection}`}
                            >
                              <td className="px-2 py-1.5 font-mono">{row.collection}</td>
                              <td className="px-2 py-1.5 text-right">{row?.expected_count ?? '-'}</td>
                              <td className="px-2 py-1.5 text-right">{row?.pre_restore_count ?? '-'}</td>
                              <td className="px-2 py-1.5 text-right">{row?.post_restore_count ?? '-'}</td>
                              <td className={`px-2 py-1.5 text-right ${varianceValue === 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                {row?.variance ?? '-'}
                              </td>
                              <td className="px-2 py-1.5 text-center">
                                <span
                                  className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold ${isPass ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'}`}
                                  data-testid={`restore-collection-count-status-${row.collection}`}
                                >
                                  {row?.verification_skipped ? 'SKIPPED' : isPass ? 'PASS' : 'FAIL'}
                                </span>
                              </td>
                              <td className="px-2 py-1.5 text-left text-[11px] text-slate-600" data-testid={`restore-collection-count-reason-${row.collection}`}>
                                {row?.error || row?.skip_reason || '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              disabled={restoreInProgress}
              onClick={() => setRestoreProgressDialogOpen(false)}
              data-testid="restore-progress-close-button"
            >
              {restoreInProgress ? 'Restore in progress...' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Backups</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to delete <strong>{selectedBackupIds.length}</strong> backup file(s) from the <strong>{folders[selectedFolder].name}</strong> tab.
              <p className="text-red-600 mt-2">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backup-bulk-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={deleting || selectedBackupIds.length === 0 || !canDeleteInSelectedFolder}
              className="bg-red-600 hover:bg-red-700"
              data-testid="backup-bulk-delete-confirm-button"
            >
              {deleting ? 'Deleting...' : `Delete ${selectedBackupIds.length} Selected`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Backup</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this backup?
              <p className="font-mono mt-2 text-sm">{backupToDelete?.name}</p>
              <p className="text-red-600 mt-2">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackupManagementPage;
